<template>
    <div class="step1-basics">
        <div :class="['field', { 'has-error': store.validationErrors.fullName }]">
            <label class="label">{{ translate("page.admin_edit_event.full_name") }}</label>
            <div class="control">
                <input
                    v-model="store.fullName"
                    class="input"
                    type="text"
                    maxlength="65"
                    required
                    :placeholder="translate('wizard.step1.name_placeholder')"
                />
            </div>
            <p v-if="store.validationErrors.fullName" class="help is-danger">
                {{ store.validationErrors.fullName[0] }}
            </p>
        </div>

        <div class="field">
            <label class="label">{{ translate("page.admin.edit_event.event_type") }}</label>
            <div class="control">
                <MultiselectInput
                    v-model="selectedEventTypeObjects"
                    :options="eventTypeOptions"
                    :placeholder="translate('page.admin.edit_event.select_event_type')"
                    multiple
                />
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <div :class="['field', { 'has-error': store.validationErrors.startDate }]">
                    <label class="label">{{ translate("page.admin_edit_event.event_date") }}</label>
                    <div class="control">
                        <DateTimePicker class="input" v-model="startDateLocal" />
                    </div>
                    <p v-if="store.validationErrors.startDate" class="help is-danger">
                        {{ store.validationErrors.startDate[0] }}
                    </p>
                </div>
            </div>
            <div class="column">
                <div class="field">
                    <label class="label">{{ translate("page.admin_edit_event.event_end_date") }}</label>
                    <div class="control">
                        <DateTimePicker class="input" v-model="endDateLocal" />
                    </div>
                </div>
            </div>
        </div>

        <div class="field mt-5">
            <button class="button is-primary is-fullwidth" data-testid="wizard-continue" @click="onSubmit">
                {{ translate("common.continue") }} <i class="fas fa-arrow-right ml-2"></i>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import DateTimePicker from "../../../Common/DateTimePicker.vue";
    import MultiselectInput from "../../../Common/MultiselectInput.vue";
    import { convertToTimezone, convertFromTimezone } from "../../../Common/dateTimeUtils";
    import { EVENT_TYPES } from "../../../Common/consts";
    import { translate } from "../../../Common/translations";
    import { useWizardStore } from "../wizardStore";
    import { validateStep1 } from "../wizardValidation";
    import { convertEventTypesToObjects, type EventTypeObject } from "../../editEventStore";

    const emit = defineEmits<{
        submit: [];
    }>();

    const store = useWizardStore();

    const eventTypeOptions = convertEventTypesToObjects(EVENT_TYPES);

    const selectedEventTypeObjects = computed({
        get() {
            return convertEventTypesToObjects(store.eventTypes);
        },
        set(value: EventTypeObject[]) {
            store.eventTypes = value.map(t => t.key);
        },
    });

    const startDateLocal = computed({
        get() {
            return convertToTimezone(store.startDate, store.timezone, "yyyy-MM-dd");
        },
        set(value: string) {
            store.startDate = convertFromTimezone(value + " 00:00:00", store.timezone);
            store.endDate = convertFromTimezone(value + " 23:59:59", store.timezone);
        },
    });

    const endDateLocal = computed({
        get() {
            return convertToTimezone(store.endDate, store.timezone, "yyyy-MM-dd");
        },
        set(value: string) {
            store.endDate = convertFromTimezone(value + " 23:59:59", store.timezone);
        },
    });

    function onSubmit() {
        const errors = validateStep1({ fullName: store.fullName, startDate: store.startDate });
        const rest = { ...store.validationErrors };
        delete rest.fullName;
        delete rest.startDate;
        store.validationErrors = Object.keys(errors).length > 0 ? { ...rest, ...errors } : rest;
        if (Object.keys(errors).length > 0) {
            return;
        }
        emit("submit");
    }
</script>
